import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Horisontell split-pane med dragbar divider. Vänster+höger barn får
 * procentuell bredd. Sparar sitt split-förhållande (0.2 - 0.7) i
 * `localStorage` under angiven nyckel så layouten överlever reload.
 */
export function SplitPane({
  storageKey,
  defaultLeftFraction = 0.35,
  minLeftFraction = 0.2,
  maxLeftFraction = 0.7,
  left,
  right,
}: {
  storageKey: string;
  defaultLeftFraction?: number;
  minLeftFraction?: number;
  maxLeftFraction?: number;
  left: React.ReactNode;
  right: React.ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const [fraction, setFraction] = useState<number>(() => {
    if (typeof window === "undefined") return defaultLeftFraction;
    const raw = window.localStorage.getItem(storageKey);
    const n = raw ? parseFloat(raw) : NaN;
    if (!Number.isFinite(n)) return defaultLeftFraction;
    return Math.max(minLeftFraction, Math.min(maxLeftFraction, n));
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageKey, String(fraction));
    }
  }, [storageKey, fraction]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const rel = (e.clientX - rect.left) / rect.width;
      const clamped = Math.max(minLeftFraction, Math.min(maxLeftFraction, rel));
      setFraction(clamped);
    };
    const onUp = () => {
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [minLeftFraction, maxLeftFraction]);

  const leftPct = `${(fraction * 100).toFixed(2)}%`;
  const rightPct = `${((1 - fraction) * 100).toFixed(2)}%`;

  return (
    <div
      ref={containerRef}
      className="flex min-h-0 flex-1 overflow-hidden"
    >
      <div style={{ width: leftPct }} className="flex min-h-0 min-w-0 flex-col">
        {left}
      </div>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Dra för att ändra delning"
        onMouseDown={onMouseDown}
        className="w-1 shrink-0 cursor-col-resize bg-slate-200 transition-colors hover:bg-sky-400"
      />
      <div style={{ width: rightPct }} className="flex min-h-0 min-w-0 flex-col">
        {right}
      </div>
    </div>
  );
}
